import * as React from 'react';

import uniqueId from '../../common/uniqueId';
import { Button, ButtonPriority } from '../Button';
import { st, classes } from './InputDialog.st.css';
import { TPAComponentsConsumer } from '../TPAComponentsConfig';
import { Dialog } from '../Dialog';
import {
  InputDialogProps,
  InputDialogDefaultProps,
  InputDialogDataHooks,
} from './types';

/** A dialog component that consists of a header and footer with action buttons, and might have overflowed content. */

export class InputDialog extends React.PureComponent<InputDialogProps> {
  private readonly _titleId: string;
  private readonly _subtitleId: string;

  constructor(props: InputDialogProps) {
    super(props);
    this._titleId = uniqueId('input-dialog-title');
    this._subtitleId = uniqueId('input-dialog-subtitle');
  }

  static displayName = 'InputDialog';
  static defaultProps: InputDialogDefaultProps = {
    withoutDivider: false,
  };

  render() {
    const {
      className,
      children,
      'data-hook': dataHook,
      isOpen,
      onClose,
      title,
      subtitle,
      primaryButton,
      secondaryButton,
      manualFocus,
      closeButtonRef,
      fullscreen,
      withoutDivider,
      ariaLabel,
      ariaLabelledBy,
      ariaDescribedBy,
      closeButtonAriaLabel,
      closeButtonAriaLabelledby,
      forceBWTheme,
      customFooter,
    } = this.props;

    return (
      <TPAComponentsConsumer>
        {({ mobile }) => (
          <Dialog
            isOpen={isOpen}
            onClose={onClose}
            className={st(
              classes.root,
              { mobile, fullscreen },
              className,
              classes.dialog,
            )}
            contentClassName={classes.dialogContent}
            childrenWrapperClassName={classes.dialogChildrenWrapper}
            data-hook={dataHook}
            manualFocus={manualFocus}
            closeButtonRef={closeButtonRef}
            aria-label={ariaLabel || title}
            aria-labelledby={
              ariaLabelledBy || title ? this._titleId : undefined
            }
            aria-describedby={
              ariaDescribedBy || subtitle ? this._subtitleId : undefined
            }
            closeButtonAriaLabel={closeButtonAriaLabel}
            closeButtonAriaLabelledby={closeButtonAriaLabelledby}
            forceBWTheme={forceBWTheme}
          >
            <div className={classes.header}>
              {title && (
                <h2 id={this._titleId} className={classes.title}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <div id={this._subtitleId} className={classes.subtitle}>
                  {subtitle}
                </div>
              )}
            </div>
            <div className={classes.content}>{children}</div>
            {!withoutDivider && <div className={classes.divider}></div>}
            {customFooter ? (
              <div className={classes.customFooterWrapper}>{customFooter}</div>
            ) : (
              <div className={classes.footer}>
                {primaryButton && (
                  <Button
                    upgrade
                    className={st(classes.button, {
                      primary: true,
                    })}
                    onClick={primaryButton.onClick}
                    disabled={primaryButton.disabled}
                    data-hook={InputDialogDataHooks.PrimaryButton}
                  >
                    {primaryButton.text}
                  </Button>
                )}
                {secondaryButton && (
                  <Button
                    upgrade
                    priority={ButtonPriority.basicSecondary}
                    className={st(classes.button, { secondary: true })}
                    onClick={secondaryButton.onClick}
                    disabled={secondaryButton.disabled}
                    data-hook={InputDialogDataHooks.SecondaryButton}
                  >
                    {secondaryButton.text}
                  </Button>
                )}
              </div>
            )}
          </Dialog>
        )}
      </TPAComponentsConsumer>
    );
  }
}
